import { useState } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { ChevronDown, Circle } from 'lucide-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useAnnotationStrokeColor, useAnnotationTool } from '../model/comments.store';
import type { AnnotatedCommentMediaKind } from '../model/annotation';
import { TOOL_SPECS_BY_TOOL } from '../lib/toolSpecs';
import { AnnotationToolbar } from './AnnotationToolbar';

interface Props {
  assetKind: AnnotatedCommentMediaKind;
  /**
   * When false, render as if no tool were selected — even if the global
   * `tool` is set by another composer. The global `tool` is single-valued
   * across all `AnnotatedCommentInput`s, so this flag prevents non-owning
   * composers (e.g. the bottom new-comment input while a root edit is open)
   * from echoing the owner's active styling.
   */
  isActive?: boolean;
  /**
   * Whether the popover should include the undo / redo / clear actions
   * section. The edit composer opts in (its inline actions row is too
   * narrow to hold them); the always-visible bottom composer opts out
   * (those actions render inline there).
   */
  showDraftActions?: boolean;
}

export function AnnotationToolButton({
  assetKind,
  isActive = true,
  showDraftActions = false,
}: Props) {
  const { t } = useTranslation('projects');
  const { tool, selectTool } = useAnnotationTool();
  const { strokeColor } = useAnnotationStrokeColor();
  const [open, setOpen] = useState(false);

  // `tool` is global; treat it as null for non-owning composers so their
  // toolbar doesn't echo another composer's active selection.
  const effectiveTool = isActive ? tool : null;
  const activeSpec = effectiveTool ? TOOL_SPECS_BY_TOOL[effectiveTool] : null;
  const ActiveIcon = activeSpec?.Icon ?? Circle;
  const toolLabel = activeSpec?.label ?? t('commentOnFile.annotationToolButton.annotationTool');
  const menuLabel = t('commentOnFile.annotationToolButton.openAnnotationTools');
  const deactivateLabel = t('commentOnFile.annotationToolButton.toolDeactivate', {
    tool: toolLabel,
  });
  // 8-digit hex appends ~25% alpha so the tinted surface stays legible
  // while clearly signaling the active color.
  const tintBg = effectiveTool ? `${strokeColor}40` : undefined;
  const iconColor = effectiveTool ? strokeColor : undefined;
  const sectionHover = effectiveTool ? 'hover:brightness-95' : 'hover:bg-accent';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div
        className="inline-flex h-8 items-stretch overflow-hidden rounded-lg"
        style={effectiveTool ? { backgroundColor: tintBg } : undefined}
      >
        {effectiveTool ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={deactivateLabel}
                aria-pressed
                className={clsx(
                  'inline-flex items-center justify-center px-2 transition-colors',
                  sectionHover,
                )}
                onClick={() => selectTool(null)}
              >
                <ActiveIcon className="size-4" style={{ color: iconColor }} />
              </button>
            </TooltipTrigger>
            <TooltipContent>{deactivateLabel}</TooltipContent>
          </Tooltip>
        ) : (
          <span
            aria-hidden
            className="inline-flex items-center justify-center px-2 text-muted-foreground"
          >
            <ActiveIcon className="size-4" />
          </span>
        )}

        <div
          className={clsx('w-px shrink-0', effectiveTool ? 'bg-white' : 'bg-transparent')}
          aria-hidden
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={menuLabel}
                aria-haspopup="menu"
                aria-expanded={open}
                className={clsx(
                  'inline-flex w-5 shrink-0 items-center justify-center transition-colors',
                  sectionHover,
                )}
              >
                <ChevronDown className="size-3.5" style={{ color: iconColor }} />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{menuLabel}</TooltipContent>
        </Tooltip>
      </div>
      <PopoverContent className="w-auto p-0" align="start" side="top">
        <AnnotationToolbar assetKind={assetKind} showDraftActions={showDraftActions} />
      </PopoverContent>
    </Popover>
  );
}
